import { Language } from '@/types/language.types'
import {
  DefaultNodeTypes,
  SerializedLinkNode,
  type DefaultTypedEditorState,
} from '@payloadcms/richtext-lexical'
import {
  JSXConvertersFunction,
  LinkJSXConverter,
  RichText as RichTextComponent,
} from '@payloadcms/richtext-lexical/react'
import { cn } from '@/lib/utils'

type NodeTypes = DefaultNodeTypes

type AllowedCollections = 'pages'

const internalDocToHref = ({
  linkNode,
  locale,
}: {
  linkNode: SerializedLinkNode
  locale?: string
}) => {
  const collectionSlugs = {
    pages: '',
  }

  const docField = linkNode.fields.doc
  if (!docField) {
    throw new Error('Missing doc field in linkNode')
  }

  const { value, relationTo } = linkNode.fields.doc!

  if (typeof value !== 'object' || !value) {
    throw new Error('Expected value to be an object')
  }
  const slug = (value as { slug?: string }).slug
  if (!slug) {
    throw new Error(`Missing slug for ${relationTo} link`)
  }

  const prefix = collectionSlugs[relationTo as AllowedCollections]
  return `/${locale ? locale : ''}${prefix}/${slug}`.replace(/\/+/, '/') // ensures no double slashes
}

const jsxConverters =
  (locale?: Language): JSXConvertersFunction<NodeTypes> =>
  ({ defaultConverters }) => ({
    ...defaultConverters,
    ...LinkJSXConverter({
      internalDocToHref: (args) => internalDocToHref({ ...args, locale }),
    }),
  })

type Props = {
  data: DefaultTypedEditorState
  enableGutter?: boolean
  enableProse?: boolean
} & React.HTMLAttributes<HTMLDivElement>

export default function RichText(props: Props) {
  const { className, enableProse = true, enableGutter = true, lang, ...rest } = props

  const locale = 'en'

  return (
    <RichTextComponent
      converters={jsxConverters(lang ? (lang as Language) : locale)}
      className={cn(
        'payload-richtext',
        {
          'container-max': enableGutter,
          'max-w-none': !enableGutter,
          'mx-auto prose md:prose-md dark:prose-invert': enableProse,
        },
        className,
      )}
      {...rest}
    />
  )
}
